import 'url-polyfill'
import 'whatwg-fetch'
import {Suspense} from 'react'
import {createRoot} from 'react-dom/client'
import {RouterProvider} from 'react-router'
import {QueryClientProvider} from '@tanstack/react-query'
import {ReactQueryDevtools} from '@tanstack/react-query-devtools'
import {fromJust} from '@freckle/maybe'
import {DelayedSpinner} from '@freckle/educator-entities/ts/common/components/spinner-wrapper/delayed-spinner'
import {fancyApiFetch} from '@freckle/educator-entities/ts/common/helpers/ajax'
import '@freckle/educator-entities/ts/common/helpers/event-polyfill'
import {setupI18n} from '@freckle/educator-entities/ts/common/helpers/translate/i18n/i18n-helper'
import {BreakpointProvider} from '@freckle/educator-materials/ts/context/breakpoint-provider'
import NotificationManager from '@freckle/classroom/ts/common/components/notifications-wrapper/notification-manager'
import {checkInternetConnectivity} from '@freckle/classroom/ts/common/helpers/api-helper'
import {addSpinner, bindReactModal} from '@freckle/classroom/ts/common/helpers/helpers'
import {queryClient} from '@freckle/classroom/ts/common/query-client'
import {initializeGlobalEventListeners} from './common/routers/init-global-event-listeners'
import {routerV2 as router} from './common/routers/router-v2'
import {ErrorBoundary} from './error-boundary'
// Important: Keep CSS import first to correctly order global CSS and CSS Modules
import './../css/freckle.scss'
import {feedbackOverlay} from './main.module.scss'

setupI18n('classroom')

//
// Bundle version update notifier
//
// Asks user to refresh page when the JS bundle is detected to be outdated
//

const bundleHtmlElement = document.querySelector('script[src^="/js/bundle"]')
if (bundleHtmlElement !== null) {
  const bundleLocation = bundleHtmlElement.getAttribute('src')
  if (bundleLocation !== null) {
    const intervalId = setInterval(() => {
      fancyApiFetch(bundleLocation, {method: 'HEAD', cache: 'no-store'}).catch(response => {
        if (response.status === 404) {
          NotificationManager.createDashboardUpdateNotification()
          window.clearInterval(intervalId)
        }
      })
    }, 60000)
  }
}

// Ensures the user's connection is healthy during Freckle usage
checkInternetConnectivity()

// initialize and render the views
initializeGlobalEventListeners()
addSpinner()
bindReactModal()

const container = fromJust(document.getElementById('freckleapp'), 'Container not found')

const root = createRoot(container)

root.render(
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <BreakpointProvider>
        {/* React Suspense is used for i18n helper and lazy usage in Router */}
        <Suspense fallback={<DelayedSpinner />}>
          <RouterProvider router={router} />
          <div id="feedback-overlay" className={feedbackOverlay} />
        </Suspense>
      </BreakpointProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </ErrorBoundary>,
)
